import { api, ApiError } from './client'
import { completeOperation, pendingCount, pendingOperations, updateOperation } from '../db/offlineDb'
import { appStore } from '../state/appStore'

export interface SyncSummary {
  synced: number
  conflicts: number
  pending: number
  firstError?: string
}

let activeSync: Promise<SyncSummary> | undefined

export function refreshPendingCount(): Promise<void> {
  return pendingCount().then(pending => appStore.patch({ pending }))
}

export function syncPending(): Promise<SyncSummary> {
  // A read session may be committed while a background sync is already running.
  // Queue a second drain instead of returning the older result and leaving the
  // freshly committed operation in IndexedDB until the next manual refresh.
  if (activeSync) return activeSync.then(() => syncPending())
  let current: Promise<SyncSummary>
  current = runSync().finally(() => {
    if (activeSync === current) activeSync = undefined
  })
  activeSync = current
  return current
}

async function runSync(): Promise<SyncSummary> {
  if (!navigator.onLine) {
    await refreshPendingCount()
    return { synced: 0, conflicts: 0, pending: await pendingCount() }
  }
  appStore.patch({ syncing: true })
  let synced = 0
  let conflicts = 0
  let firstError: string | undefined
  let existingConflictError: string | undefined
  try {
    for (const operation of await pendingOperations()) {
      if (operation.state === 'CONFLICT') {
        conflicts += 1
        existingConflictError ??= operation.lastError
        continue
      }
      operation.state = 'SENDING'
      operation.attempts += 1
      await updateOperation(operation)
      try {
        await api.ingest(operation.payload)
        await completeOperation(operation)
        synced += 1
      } catch (error) {
        operation.lastError = error instanceof Error ? error.message : 'Неизвестная ошибка'
        const permanentClientError = error instanceof ApiError && error.status >= 400 && error.status < 500
        operation.state = permanentClientError ? 'CONFLICT' : 'PENDING'
        await updateOperation(operation)
        firstError ??= operation.lastError
        if (operation.state === 'CONFLICT') conflicts += 1
        if (operation.state === 'PENDING') break
      }
    }
    await refreshPendingCount()
    return { synced, conflicts, pending: await pendingCount(), firstError: firstError ?? existingConflictError }
  } finally {
    appStore.patch({ syncing: false })
  }
}
